import { useMemo, type ReactNode } from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ReferenceLink } from './ReferenceLink'

// react-markdown sanitizes hrefs to a safe-protocol allowlist (drops unknown schemes). Let
// our `steward://` references AND `message:` (Apple Mail) links through; defer everything
// else to the default sanitizer. Without the `message:` exception the default sanitizer
// strips it and the email link renders as dead text (the brief's source-email links).
function urlTransform(url: string): string {
  return url.startsWith('steward://') || url.startsWith('message:') ? url : defaultUrlTransform(url)
}

/** Parse a `steward://<kind>/<id>` href into its parts (id is percent-encoded). */
function parseStewardRef(href: string): { kind: string; id: string } | null {
  if (!href.startsWith('steward://')) return null
  const rest = href.slice('steward://'.length)
  const slash = rest.indexOf('/')
  if (slash === -1) return null
  return { kind: rest.slice(0, slash), id: decodeURIComponent(rest.slice(slash + 1)) }
}

/**
 * Markdown renderer for the steward chat + cards. react-markdown + remark-gfm, so it
 * handles nested lists, task-list checkboxes (`- [ ]`), and tables — plus the usual
 * headings / lists / bold / italic / code / links / blockquotes. Styled via the
 * `components` map (Tailwind), so it matches the chat's look without a global prose CSS.
 * Links open in a new tab; mailto:/message:// links open in Mail.
 *
 * `renderCard`: when the chat passes this, a fenced ```steward-card\n<job path>\n``` block
 * renders as the real inline Job card (the bordered graphic) instead of a code block — the
 * model emits the fence to SHOW a job, and its commentary renders around it. The caller
 * (StewardMessage) supplies the renderer so Markdown never imports JobCard (which imports
 * Markdown — that would be a cycle), and so the card never nests inside a card's own body.
 */

interface Props {
  source: string
  className?: string
  renderCard?: (path: string) => ReactNode
}

const CARD_LANG = 'language-steward-card'

/* eslint-disable @typescript-eslint/no-explicit-any */
function makeComponents(renderCard?: (path: string) => ReactNode): Record<string, any> {
  return {
  h1: ({ children }: any) => <p className="mt-3 mb-1.5 text-base font-medium text-foreground">{children}</p>,
  h2: ({ children }: any) => <p className="mt-3 mb-1 text-[15px] font-medium text-foreground">{children}</p>,
  h3: ({ children }: any) => <p className="mt-2 mb-1 text-sm font-medium text-foreground">{children}</p>,
  p: ({ children }: any) => <p className="my-2 leading-relaxed first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }: any) => <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }: any) => <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>,
  li: ({ children, className }: any) => (
    <li className={className?.includes('task-list-item') ? 'list-none -ml-4' : undefined}>{children}</li>
  ),
  input: ({ type, checked }: any) =>
    type === 'checkbox' ? (
      <input type="checkbox" checked={!!checked} readOnly className="mr-1.5 align-middle accent-foreground" />
    ) : null,
  a: ({ href, children }: any) => {
    // A steward:// reference → a live handle (hover-peek + click-to-open card).
    const ref = typeof href === 'string' ? parseStewardRef(href) : null
    if (ref) {
      return <ReferenceLink kind={ref.kind} id={ref.id}>{children}</ReferenceLink>
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="text-foreground underline decoration-border underline-offset-2 hover:decoration-foreground"
      >
        {children}
      </a>
    )
  },
  strong: ({ children }: any) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }: any) => <em className="italic">{children}</em>,
  blockquote: ({ children }: any) => (
    <blockquote className="my-2 border-l-2 border-border pl-3 text-muted-foreground">{children}</blockquote>
  ),
  code: ({ className, children, node }: any) => {
    if (renderCard && className?.includes('steward-card')) {
      // The fenced ```steward-card``` body is the job's folder path → render the real card.
      const raw = node?.children?.[0]?.value
      const path = String(raw ?? children ?? '').trim()
      return path ? renderCard(path) : null
    }
    return className?.startsWith('language-') ? (
      <code className="font-mono text-[13px]">{children}</code>
    ) : (
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">{children}</code>
    )
  },
  pre: ({ children, node }: any) => {
    // A steward-card fence: drop the <pre> frame so the card renders unwrapped.
    const cls = node?.children?.[0]?.properties?.className
    if (renderCard && Array.isArray(cls) && cls.includes(CARD_LANG)) {
      return <>{children}</>
    }
    return (
      <pre className="my-2 overflow-x-auto rounded-md border border-border bg-muted/40 px-3 py-2 text-[13px] leading-relaxed">
        {children}
      </pre>
    )
  },
  table: ({ children }: any) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-[13px]">{children}</table>
    </div>
  ),
    th: ({ children }: any) => <th className="border border-border px-2 py-1 text-left font-medium">{children}</th>,
    td: ({ children }: any) => <td className="border border-border px-2 py-1 align-top">{children}</td>,
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function Markdown({ source, className, renderCard }: Props) {
  const components = useMemo(() => makeComponents(renderCard), [renderCard])
  // Strip HTML comments (<!-- ... -->) before rendering. react-markdown has no rehype-raw here,
  // so raw-HTML nodes are emitted as literal text — meaning a note's authoring preamble (e.g.
  // project.md / job.md's "<!-- governing note … -->") would otherwise show verbatim in the card.
  // These annotations are never meant to render. (steward-card fences use ```, not <!--, so the
  // card fence is untouched.)
  const cleaned = useMemo(() => source.replace(/<!--[\s\S]*?-->/g, ''), [source])
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={urlTransform}>
        {cleaned}
      </ReactMarkdown>
    </div>
  )
}
